// Purpose: Defines BayHive settings navigation, access rules, and native or embedded section keys.
const SETTINGS_ACCESS_PERMISSION_HANDLES = [
  'bh-view-settings',
  'manage-settings',
  'manage-settings-main',
  'manage-settings-groups-regions',
  'manage-settings-data-scoping',
  'manage-users',
  'manage-users-non-admin',
  'manage-roles',
  'manage-settings-time-clock',
  'manage-settings-scheduler-setup',
  'manage-workflow',
  'manage-settings-repair-order-setup',
  'manage-settings-repair-order-notes',
  'manage-settings-labor-rates',
  'manage-settings-labels',
  'bh-manage-market-sources',
  'bh-edit-tax-rates',
  'manage-dvi-setup',
  'manage-dvi-sheets',
  'manage-dvi-notes',
  'manage-settings-dvi-worksheet',
  'manage-settings-quality-control-setup',
  'bh-fee-list',
  'bh-time-clock-configure',
];

export const SETTINGS_ALL_SHOPS_QUERY_VALUE = 'all-shops';

export const settingsSections = [
  {
    key: 'main',
    group: 'Shop Settings',
    label: 'Main',
    icon: 'fa-solid fa-sliders',
    description: 'General shop and account configuration.',
    sourceComponent: 'ShopManagement/MainSettings',
    // The Autoflow page stays the Autoflow-shell destination for this section.
    legacyRouteName: 'settings.index',
    permissionHandles: ['manage-settings', 'manage-settings-main'],
    status: 'native',
  },
  {
    key: 'theming',
    group: 'Shop Settings',
    label: 'Branding',
    icon: 'fa-solid fa-palette',
    description: 'Your shop’s logos, colors, fonts, and button style.',
    sourceComponent: 'ShopManagement/Theming',
    permissionHandles: ['manage-settings', 'manage-settings-main'],
    status: 'native',
  },
  {
    key: 'customer-view',
    group: 'Shop Settings',
    label: 'Customer View',
    icon: 'fa-solid fa-share-nodes',
    description: 'Customer visibility, repair progress, and customer preview.',
    sourceComponent: 'ShopManagement/CustomerViewSettings',
    permissionHandles: ['manage-settings', 'manage-settings-main', 'manage-workflow'],
    status: 'native',
  },
  {
    key: 'regulatory-compliance',
    group: 'Shop Settings',
    label: 'Regulatory Compliance',
    icon: 'fa-solid fa-file-shield',
    description: 'A2P business and designated-contact information for regulatory compliance.',
    sourceComponent: 'ShopManagement/RegulatoryCompliance',
    permissionHandles: ['manage-settings', 'manage-settings-main'],
    status: 'native',
  },
  {
    key: 'organization.groups-regions',
    group: 'Organization',
    label: 'Groups / Regions',
    icon: 'fa-solid fa-layer-group',
    description: 'Operational group and region structure for account-wide management.',
    sourceComponent: 'ShopManagement/Organization/GroupsRegions',
    permissionHandles: ['manage-settings', 'manage-settings-groups-regions'],
    status: 'native',
    supportsAllShopsScope: true,
    msoOnly: true,
  },
  {
    key: 'organization.data-scoping',
    group: 'Organization',
    label: 'Customer Scoping',
    icon: 'fa-solid fa-share-nodes',
    description: 'Define whether customers are shared at the account, MSO group, or shop level.',
    sourceComponent: 'ShopManagement/Organization/CustomerScoping',
    permissionHandles: ['manage-settings', 'manage-settings-data-scoping'],
    status: 'native',
    supportsAllShopsScope: true,
    msoOnly: true,
  },
  {
    key: 'users-permissions',
    group: 'Organization',
    label: 'Users',
    icon: 'fa-solid fa-users',
    description: 'Multi-location users, local access, permissions, and reporting lines.',
    sourceComponent: 'ShopManagement/Users',
    permissionHandles: ['manage-settings', 'manage-users', 'manage-users-non-admin'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'users-permissions.roles',
    group: 'Organization',
    label: 'Roles',
    icon: 'fa-solid fa-shield-halved',
    description: 'Role templates and permission bundles.',
    sourceComponent: 'ShopManagement/Roles',
    permissionHandles: ['manage-settings', 'manage-roles'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'time-clock',
    group: 'Shop Settings',
    label: 'Time Clock',
    contextLabel: 'Time Clock',
    contextKeys: ['time-clock'],
    icon: 'fa-solid fa-user-clock',
    description: 'Pay periods, break types, location requirements, and clock-in exceptions.',
    sourceComponent: 'ShopManagement/TimeClockSettings',
    // The Autoflow equivalent of this section, for resolveBrandSettingsUrl:
    // this settings workspace is unreachable outside the BayHive shell.
    legacyRouteName: 'settings.time-clock',
    // Deliberately omits 'manage-settings': Configure Time Clock is granted from
    // Navigation alongside the Time Clock page, not from Settings.
    permissionHandles: ['manage-settings-time-clock', 'bh-time-clock-configure'],
    status: 'native',
  },
  {
    key: 'workflow',
    group: 'Shop Settings',
    subgroup: 'Dashboard',
    subgroupKey: 'dashboard',
    subgroupIcon: 'fa-solid fa-gauge-high',
    label: 'Workflow Setup',
    contextLabel: 'Dashboard',
    contextKeys: ['dashboard'],
    icon: 'fa-solid fa-diagram-project',
    description: 'Workflow status and predefined process configuration.',
    legacyRouteName: 'workflow.setup',
    legacyViewKey: 'settings.workflow',
    nativeSupplementComponent: 'ShopManagement/WorkflowSharingSettings',
    permissionHandles: ['manage-settings', 'manage-workflow'],
    status: 'legacy',
  },
  {
    key: 'scheduler-setup',
    group: 'Shop Settings',
    subgroup: 'Scheduling',
    subgroupKey: 'scheduling',
    subgroupIcon: 'fa-solid fa-calendar-days',
    label: 'Scheduler Setup',
    contextLabel: 'Scheduler Setup',
    contextKeys: ['scheduler-setup'],
    icon: 'fa-solid fa-calendar-check',
    description: 'Kiosk, appointment, bay, service, and scheduler link configuration.',
    legacyViewKey: 'settings.scheduler-setup',
    permissionHandles: ['manage-settings', 'manage-settings-scheduler-setup'],
    status: 'legacy',
  },
  {
    key: 'integrations',
    group: 'Shop Settings',
    label: 'Integrations',
    contextLabel: 'Integrations',
    icon: 'fa-solid fa-plug',
    description: 'Third-party services this shop connects to: labor guides and parts catalogues.',
    sourceComponent: 'ShopManagement/Integrations',
    permissionHandles: ['manage-settings', 'manage-settings-repair-order-setup'],
    status: 'native',
  },
  {
    key: 'ro.setup',
    group: 'Repair Order',
    label: 'Repair Order Setup',
    contextLabel: 'Repair Orders',
    contextKeys: ['repair-orders', 'repair-order-detail'],
    icon: 'fa-solid fa-file-lines',
    description: 'Repair order defaults, setup, and configuration.',
    legacyRouteName: 'repair-orders.setup',
    legacyViewKey: 'settings.repair-order-setup',
    // Financials stays as a native card beneath the embedded Autoflow view. The
    // labor guide and parts catalogues moved to Integrations once they had
    // credentials attached — "where do I connect a vendor" deserves one obvious
    // home, and it is not a repair-order setting.
    nativeSupplementComponent: 'ShopManagement/RepairOrderSettingsSupplement',
    permissionHandles: ['manage-settings', 'manage-settings-repair-order-setup'],
    status: 'legacy',
  },
  {
    key: 'ro.deferred-services',
    group: 'Repair Order',
    label: 'Deferred Services',
    icon: 'fa-solid fa-clock-rotate-left',
    description: 'Completion reasons for deferred services and their shop assignments.',
    sourceComponent: 'ShopManagement/DeferredServices',
    permissionHandles: ['manage-settings', 'manage-settings-repair-order-setup'],
    status: 'native',
    supportsAllShopsScope: true,
    clientOnly: true,
  },
  {
    key: 'ro.repair-order-notes',
    group: 'Repair Order',
    label: 'Repair Order Notes',
    contextLabel: 'Repair Order Notes',
    contextKeys: ['repair-order-notes', 'work-order-notes'],
    icon: 'fa-solid fa-note-sticky',
    description: 'Predefined work order and technician notes.',
    legacyRouteName: 'work-orders.notes.setup',
    legacyViewKey: 'settings.repair-order-notes',
    permissionHandles: ['manage-settings', 'manage-settings-repair-order-notes'],
    status: 'legacy',
  },
  {
    key: 'labor-rates',
    group: 'Repair Order',
    label: 'Labor Rates',
    icon: 'fa-solid fa-screwdriver-wrench',
    description: 'Customer-facing hourly labor rates by account, group, or shop scope.',
    sourceComponent: 'ShopManagement/LaborRates',
    permissionHandles: ['manage-settings', 'manage-settings-labor-rates'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'ro.labels',
    group: 'Repair Order',
    label: 'Labels',
    icon: 'fa-solid fa-bookmark',
    description: 'Labels available on repair orders for each shop.',
    sourceComponent: 'ShopManagement/Labels',
    permissionHandles: ['manage-settings', 'manage-settings-labels'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'ro.market-sources',
    group: 'Repair Order',
    label: 'Market Sources',
    icon: 'fa-solid fa-bullhorn',
    description: 'How customers heard about the shop, offered at repair-order intake.',
    sourceComponent: 'ShopManagement/MarketSources',
    permissionHandles: ['manage-settings', 'bh-manage-market-sources'],
    status: 'native',
    // At All Shops scope the page also offers the MSO sharing workspace.
    supportsAllShopsScope: true,
  },
  {
    key: 'ro.fees',
    group: 'Repair Order',
    label: 'Fees',
    contextLabel: 'Fees',
    contextKeys: ['fees', 'fee-setup'],
    icon: 'fa-solid fa-receipt',
    description: 'Reusable shop fees, defaults, taxability, and location assignments.',
    sourceComponent: 'ShopManagement/Fees',
    permissionHandles: ['bh-fee-list'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'ro.discounts',
    group: 'Repair Order',
    label: 'Discounts',
    contextLabel: 'Discounts',
    contextKeys: ['discounts', 'discount-setup'],
    icon: 'fa-solid fa-tags',
    description: 'Reusable discounts, eligible sales, and location availability.',
    sourceComponent: 'ShopManagement/Discounts',
    permissionHandles: ['manage-settings'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'inventory.parts-matrices',
    group: 'Shop Settings',
    subgroup: 'Inventory',
    subgroupKey: 'inventory',
    subgroupIcon: 'fa-solid fa-boxes-stacked',
    label: 'Parts Matrices',
    contextLabel: 'Parts Matrices',
    contextKeys: ['parts-matrices', 'parts-pricing'],
    icon: 'fa-solid fa-table-cells',
    description: 'Parts pricing by vendor, part type, vehicle make, and cost range.',
    sourceComponent: 'ShopManagement/PartsMatrices',
    permissionHandles: ['manage-settings'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'ro.taxes',
    group: 'Repair Order',
    label: 'Taxes',
    contextLabel: 'Taxes',
    contextKeys: ['taxes', 'tax-setup'],
    icon: 'fa-solid fa-percent',
    description: 'Shop-level tax rates, taxable categories, caps, defaults, and active status.',
    sourceComponent: 'ShopManagement/Taxes',
    permissionHandles: ['manage-settings', 'bh-edit-tax-rates'],
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'ro.payment-types',
    group: 'Shop Settings',
    subgroup: 'Payments',
    subgroupKey: 'payments',
    subgroupIcon: 'fa-solid fa-money-check-dollar',
    label: 'Payment Types',
    contextLabel: 'Payment Types',
    contextKeys: ['payment-types'],
    icon: 'fa-solid fa-credit-card',
    description: 'Shared payment methods, categories, location assignments, and active status.',
    sourceComponent: 'ShopManagement/PaymentTypes',
    status: 'native',
    supportsAllShopsScope: true,
  },
  {
    key: 'ro.payment-terms',
    group: 'Shop Settings',
    subgroup: 'Payments',
    subgroupKey: 'payments',
    subgroupIcon: 'fa-solid fa-money-check-dollar',
    label: 'Payment Terms',
    contextLabel: 'Payment Terms',
    contextKeys: ['payment-terms'],
    icon: 'fa-solid fa-calendar-days',
    description: 'Reusable vendor payment terms, due-date rules, and location assignments.',
    sourceComponent: 'ShopManagement/PaymentTerms',
    permissionHandles: ['manage-settings'],
    requiredCapability: 'vendorInvoiceTerms',
    status: 'native',
    clientOnly: true,
    supportsAllShopsScope: true,
  },
  {
    key: 'inventory.tracking',
    group: 'Shop Settings',
    subgroup: 'Inventory',
    subgroupKey: 'inventory',
    subgroupIcon: 'fa-solid fa-boxes-stacked',
    label: 'Inventory Tracking',
    contextLabel: 'Inventory Tracking',
    contextKeys: ['inventory'],
    icon: 'fa-solid fa-sliders',
    description: 'Inventory tracking and parts at invoicing.',
    sourceComponent: 'ShopManagement/Inventory/InventorySettings',
    permissionHandles: ['manage-settings', 'manage-settings-repair-order-setup'],
    status: 'native',
    clientOnly: true,
  },
  {
    key: 'purchasing.email-template',
    group: 'Shop Settings',
    subgroup: 'Inventory',
    subgroupKey: 'inventory',
    subgroupIcon: 'fa-solid fa-boxes-stacked',
    label: 'Purchase Order Email',
    contextLabel: 'Purchase Order Email',
    contextKeys: ['purchase-order-email'],
    icon: 'fa-solid fa-envelope',
    description: 'Shop email subject and message for purchase orders sent to vendors.',
    sourceComponent: 'ShopManagement/PurchaseOrderEmailTemplate',
    permissionHandles: ['manage-settings'],
    status: 'native',
    clientOnly: true,
  },
  {
    key: 'messages.wall-messages',
    group: 'Shop Settings',
    subgroup: 'Messages',
    subgroupKey: 'messages',
    subgroupIcon: 'fa-solid fa-messages',
    label: 'Wall Messages',
    contextLabel: 'Wall Messages',
    contextKeys: ['wall-messages'],
    icon: 'fa-solid fa-message',
    description: 'Post and manage wall messages for the selected shop.',
    legacyRouteName: 'wall-messages.index',
    legacyViewKey: 'settings.wall-messages',
    permissionHandles: ['manage-settings'],
    status: 'legacy',
  },
  {
    key: 'dvi.configuration',
    group: 'Shop Settings',
    subgroup: 'Vehicle Inspection',
    subgroupKey: 'dvi',
    subgroupIcon: 'fa-solid fa-camera-viewfinder',
    label: 'Configuration',
    contextLabel: 'Vehicle Inspection',
    contextKeys: ['dvi'],
    icon: 'fa-solid fa-camera-viewfinder',
    description: 'Vehicle Inspection setup, defaults, and behavior.',
    legacyRouteName: 'dvi.setup',
    legacyViewKey: 'settings.dvi-configuration',
    permissionHandles: ['manage-settings', 'manage-dvi-setup'],
    status: 'legacy',
  },
  {
    key: 'dvi.sheets',
    group: 'Shop Settings',
    subgroup: 'Vehicle Inspection',
    subgroupKey: 'dvi',
    subgroupIcon: 'fa-solid fa-camera-viewfinder',
    label: 'Sheets',
    contextLabel: 'Vehicle Inspection Sheets',
    contextKeys: ['dvi-sheets'],
    icon: 'fa-solid fa-layer-group',
    description: 'Vehicle Inspection sheet management and structure.',
    legacyRouteName: 'dvi.sheets.index',
    legacyViewKey: 'settings.dvi-sheets',
    permissionHandles: ['manage-settings', 'manage-dvi-sheets'],
    status: 'legacy',
  },
  {
    key: 'dvi.dvi-notes',
    group: 'Shop Settings',
    subgroup: 'Vehicle Inspection',
    subgroupKey: 'dvi',
    subgroupIcon: 'fa-solid fa-camera-viewfinder',
    label: 'Notes',
    contextLabel: 'Vehicle Inspection Notes',
    contextKeys: ['dvi-notes'],
    icon: 'fa-solid fa-notes-medical',
    description: 'Predefined Vehicle Inspection notes and note groups.',
    legacyRouteName: 'dvi.notes.index',
    sourceComponent: 'ShopManagement/DviNotes',
    permissionHandles: ['manage-settings', 'manage-dvi-notes'],
    status: 'native',
  },
  {
    key: 'dvi.worksheet-setup',
    group: 'Shop Settings',
    subgroup: 'Vehicle Inspection',
    subgroupKey: 'dvi',
    subgroupIcon: 'fa-solid fa-camera-viewfinder',
    label: 'Worksheet Setup',
    contextLabel: 'Worksheet Setup',
    contextKeys: ['worksheet-setup'],
    icon: 'fa-solid fa-clipboard-list',
    description: 'Worksheet setup reused under the Vehicle Inspection settings area.',
    legacyRouteName: 'worksheet.sheets.setup',
    legacyViewKey: 'settings.worksheet-setup',
    permissionHandles: ['manage-settings', 'manage-settings-dvi-worksheet'],
    status: 'legacy',
  },
  {
    key: 'quality-control',
    group: 'Shop Settings',
    subgroup: 'Quality Control',
    subgroupKey: 'quality-control',
    subgroupIcon: 'fa-solid fa-badge-check',
    label: 'Quality Control Setup',
    contextLabel: 'Quality Control',
    contextKeys: ['quality-control'],
    icon: 'fa-solid fa-badge-check',
    description: 'QC setup, workflow, and signoff configuration.',
    legacyRouteName: 'quality-control.setup',
    legacyViewKey: 'settings.quality-control',
    permissionHandles: ['manage-settings', 'manage-settings-quality-control-setup'],
    status: 'legacy',
  },
];

const settingsSectionsByKey = settingsSections.reduce((sections, section) => {
  sections[section.key] = section;
  return sections;
}, {});

const settingsSectionsByContext = settingsSections.reduce((sections, section) => {
  [section.key, ...(section.contextKeys || [])].forEach((contextKey) => {
    sections[contextKey] = section;
  });

  return sections;
}, {});

const settingsGroupOrder = ['Organization', 'Shop Settings'];

const settingsMenuItemOrder = {
  'Shop Settings': [
    'main',
    'theming',
    'customer-view',
    'regulatory-compliance',
    'settings-subgroup-dashboard',
    'settings-subgroup-scheduling',
    'settings-subgroup-repair-order',
    'settings-subgroup-inventory',
    'settings-subgroup-payments',
    'settings-subgroup-messages',
    'settings-subgroup-dvi',
    'settings-subgroup-quality-control',
    'time-clock',
  ],
  Organization: [
    'organization.groups-regions',
    'organization.data-scoping',
    'users-permissions',
    'users-permissions.roles',
  ],
  'Repair Order': [
    'ro.setup',
    'ro.deferred-services',
    'ro.repair-order-notes',
    'labor-rates',
    'ro.labels',
    'ro.fees',
    'ro.discounts',
    'ro.taxes',
  ],
};

const REPAIR_ORDER_GROUP = 'Repair Order';

function orderSettingsMenuItems(group, items) {
  const order = settingsMenuItemOrder[group] || [];
  const positions = new Map(order.map((key, index) => [key, index]));

  return [...items].sort(
    (left, right) =>
      (positions.get(left.key) ?? Number.MAX_SAFE_INTEGER) -
      (positions.get(right.key) ?? Number.MAX_SAFE_INTEGER),
  );
}

function buildSettingsMenuItems(sections) {
  const items = [];
  const subgroups = {};

  sections.forEach((section) => {
    const menuItem = {
      key: section.key,
      label: section.label,
      icon: section.icon,
    };

    if (!section.subgroup) {
      items.push(menuItem);
      return;
    }

    if (!subgroups[section.subgroupKey]) {
      subgroups[section.subgroupKey] = {
        key: `settings-subgroup-${section.subgroupKey}`,
        label: section.subgroup,
        icon: section.subgroupIcon,
        items: [],
      };
      items.push(subgroups[section.subgroupKey]);
    }

    subgroups[section.subgroupKey].items.push(menuItem);
  });

  return items;
}

export function normalizeSettingsSection(section) {
  return settingsSectionsByKey[section] ? section : 'main';
}

export function findSettingsSection(section) {
  return settingsSectionsByKey[normalizeSettingsSection(section)];
}

export function findSettingsSectionForContext(contextKey) {
  return settingsSectionsByContext[contextKey] || null;
}

/** Sections the shop selector's scope shows in the menu: MSO-only ones need the all-shops scope. */
export function visibleSettingsSections({ isAllShops = false, context = null, includeUnavailable = false } = {}) {
  return settingsSections.filter((section) => (!section.msoOnly || isAllShops)
    && (includeUnavailable || settingsSectionSupported(section, context)));
}

function settingsSectionSupported(section, context) {
  return !section.requiredCapability || context?.[section.requiredCapability]?.enabled === true;
}

/** The menu path to a section, for example `Shop Settings › Repair Order › Taxes`. */
export function settingsSectionTrail(section) {
  const isRepairOrder = section.group === REPAIR_ORDER_GROUP;

  return [
    isRepairOrder ? 'Shop Settings' : section.group,
    isRepairOrder ? REPAIR_ORDER_GROUP : section.subgroup,
    section.label,
  ]
    .filter(Boolean)
    .join(' › ');
}

export function settingsSectionGroups({ isAllShops = false, context = null } = {}) {
  const visibleSections = visibleSettingsSections({ isAllShops, context });
  const repairOrderItems = orderSettingsMenuItems(
    REPAIR_ORDER_GROUP,
    buildSettingsMenuItems(
      visibleSections.filter((section) => section.group === REPAIR_ORDER_GROUP),
    ),
  );

  return settingsGroupOrder
    .map((title) => {
      const sections = visibleSections.filter((section) => section.group === title);
      const items = buildSettingsMenuItems(sections);

      if (title === 'Shop Settings' && repairOrderItems.length > 0) {
        const repairOrderMenu = {
          key: 'settings-subgroup-repair-order',
          label: REPAIR_ORDER_GROUP,
          icon: 'fa-solid fa-clipboard-list-check',
          items: repairOrderItems,
        };
        const dviIndex = items.findIndex((item) => item.key === 'settings-subgroup-dvi');

        if (dviIndex === -1) {
          items.push(repairOrderMenu);
        } else {
          items.splice(dviIndex, 0, repairOrderMenu);
        }
      }

      return { title, items: orderSettingsMenuItems(title, items) };
    })
    .filter((group) => group.items.length > 0);
}

export function permissionHandlesFromProps(permissions = []) {
  const entries = Array.isArray(permissions) ? permissions : Object.values(permissions || {});

  return entries
    .map((permission) => (typeof permission === 'string' ? permission : permission?.handle))
    .filter(Boolean);
}

export function canAccessSettings({ user, permissionHandles = [] }) {
  if (user?.super_admin === true || Number(user?.super_admin) === 1) {
    return true;
  }

  return SETTINGS_ACCESS_PERMISSION_HANDLES.some((handle) => permissionHandles.includes(handle));
}

export function canAccessSettingsSection(sectionKey, context) {
  const section = findSettingsSection(sectionKey);

  if (!section || !settingsSectionSupported(section, context) || !canAccessSettings(context)) {
    return false;
  }

  if (!section.permissionHandles?.length) {
    return true;
  }

  if (context.user?.super_admin === true || Number(context.user?.super_admin) === 1) {
    return true;
  }

  return section.permissionHandles.some((handle) => context.permissionHandles.includes(handle));
}

export function resolveLegacySettingsUrl(sectionKey, shopId) {
  const section = findSettingsSection(sectionKey);

  if (!section?.legacyRouteName || !shopId) {
    return null;
  }

  try {
    return route(section.legacyRouteName, { shop_id: shopId });
  } catch (error) {
    return null;
  }
}

export function buildShopManagementSettingsUrl(shopId, section = 'main', options = {}) {
  const normalizedSection = normalizeSettingsSection(section);
  const baseUrl = route('shop-management.page', { shop_id: shopId, page: 'settings' });
  const currentUrl = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const scope = Object.prototype.hasOwnProperty.call(options, 'scope')
    ? options.scope
    : currentUrl?.searchParams.get('scope');
  const selectedShop = Object.prototype.hasOwnProperty.call(options, 'selectedShop')
    ? options.selectedShop
    : null;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(baseUrl, origin);

  url.searchParams.set('section', normalizedSection);

  if (scope === SETTINGS_ALL_SHOPS_QUERY_VALUE) {
    url.searchParams.set('scope', SETTINGS_ALL_SHOPS_QUERY_VALUE);
  } else {
    url.searchParams.delete('scope');
  }

  if (selectedShop) {
    url.searchParams.set('selected_shop', selectedShop);
  } else {
    url.searchParams.delete('selected_shop');
  }

  if (normalizedSection === 'scheduler-setup' && options.schedulerPanel) {
    url.searchParams.set('scheduler_panel', options.schedulerPanel);
  }

  if (normalizedSection === 'scheduler-setup' && options.schedulerJobDraft?.title) {
    url.searchParams.set('scheduler_job_name', options.schedulerJobDraft.title);

    if (options.schedulerJobDraft.icon) {
      url.searchParams.set('scheduler_job_icon', options.schedulerJobDraft.icon);
    }

    if (options.schedulerJobDraft.serviceTileId) {
      url.searchParams.set('source_service_tile', options.schedulerJobDraft.serviceTileId);
    }
  }

  return url.toString();
}

/**
 * The settings URL for a section in the shell the user is actually in.
 *
 * Every `shop-management.*` route 403s outside the BayHive shell — the brand is
 * decided by the request host, before any per-route exception runs — so the
 * Autoflow shell has to be sent to that section's own Autoflow page instead.
 * Returns null for a section with no Autoflow equivalent, which is the signal
 * to hide the entry point rather than offer a link that dead-ends.
 */
export function resolveBrandSettingsUrl(shopId, section, { isBayHive = true, ...options } = {}) {
  return isBayHive
    ? buildShopManagementSettingsUrl(shopId, section, options)
    : resolveLegacySettingsUrl(section, shopId);
}
